import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import AppContext from 'AppContext';
import GrpButton from 'Library/GrpButton/GrpButton';
import GrpSpinner from 'Components/GrpSpinner/GrpSpinner';
import {
  getTemplates,
  getReportTypes,
  getFundsAndClasses,
  getIsoLanguages,
  saveTemplate,
  deleteTemplate,
} from 'Services/overviewApi';
import classes from './TemplateManagement.module.scss';

const TemplateManagement = () => {
  const context = useContext(AppContext);
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);
  const gridRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [templateList, setTemplateList] = useState([]);
  const [reportTypes, setReportTypes] = useState([]);
  const [fundsAndClasses, setFundsAndClasses] = useState([]);
  const [languages, setLanguages] = useState([]);

  // Create form state
  const [newTemplate, setNewTemplate] = useState({
    TemplateName: '',
    ReportTypeId: '',
    AllFunds: true,
    ScopeMap: [],
    LanguageMap: [],
  });
  const [newFile, setNewFile] = useState(null);
  const [newFileName, setNewFileName] = useState('');

  // Edit modal state
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [editFile, setEditFile] = useState(null);
  const [editFileName, setEditFileName] = useState('');

  // Delete modal state
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [templatesRes, reportTypesRes, fundsRes, langsRes] = await Promise.all([
        getTemplates(),
        getReportTypes(),
        getFundsAndClasses(),
        getIsoLanguages(),
      ]);

      const templates = Array.isArray(templatesRes) ? templatesRes
        : (templatesRes.value || templatesRes.Content || []);
      setTemplateList(templates);

      const rtData = Array.isArray(reportTypesRes) ? reportTypesRes
        : (reportTypesRes.value || reportTypesRes.Content || []);
      // Filter to report types that have PDF format
      const pdfReportTypes = rtData.filter((rt) =>
        rt.DocumentFormatTypes?.some((dft) => dft.DocumentFormatTypeName === 'PDF')
      );
      setReportTypes(pdfReportTypes.length > 0 ? pdfReportTypes : rtData);

      const fundsData = fundsRes.Content || fundsRes.value || fundsRes || [];
      setFundsAndClasses(Array.isArray(fundsData) ? fundsData : []);

      const langsData = langsRes.value || langsRes.Content || [];
      setLanguages(langsData);
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>{err.human || 'Failed to load template data'}</> });
    } finally {
      setLoading(false);
    }
  }, [context.handlers]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US');
  };

  const getReportTypeName = (reportTypeId) => {
    const rt = reportTypes.find((r) => r.ReportTypeId === reportTypeId);
    return rt?.Label || rt?.ReportTypeName || '-';
  };

  const getTargetName = (template) => {
    if (template.AllFunds) return 'Default - All';
    if (!template.ScopeMap || template.ScopeMap.length === 0) return '-';
    if (template.ScopeMap.length === 1) {
      const scope = template.ScopeMap[0];
      if (!scope.ClassCode) {
        const fund = fundsAndClasses.find((f) => f.FundCode === scope.FundCode);
        return fund?.FundName || scope.FundCode;
      }
      const fund = fundsAndClasses.find((f) => f.FundCode === scope.FundCode);
      if (fund) {
        const cls = fund.Classes?.find((c) => c.ClassCode === scope.ClassCode);
        return cls?.ClassName || scope.ClassCode;
      }
      return scope.ClassCode;
    }
    let count = 0;
    template.ScopeMap.forEach((scope) => {
      if (!scope.ClassCode) {
        const fund = fundsAndClasses.find((f) => f.FundCode === scope.FundCode);
        count += fund?.Classes?.length || 1;
      } else {
        count += 1;
      }
    });
    return `Specified (${count})`;
  };

  const columnDefs = [
    {
      headerName: '',
      width: 70,
      sortable: false,
      filter: false,
      cellRenderer: (params) => (
        <div className={classes.actionIcons}>
          <span
            className="far fa-edit"
            title="Edit"
            onClick={() => handleEditClick(params.data)}
            style={{ cursor: 'pointer', fontSize: '15px' }}
          />
          <span style={{ width: '8px', display: 'inline-block' }} />
          <span
            className="far fa-trash"
            title="Delete"
            onClick={() => handleDeleteClick(params.data)}
            style={{ cursor: 'pointer', fontSize: '15px' }}
          />
        </div>
      ),
    },
    {
      headerName: 'Template Name',
      field: 'TemplateName',
      width: 180,
      cellRenderer: (params) => params.value || '-',
    },
    {
      headerName: 'Created Date',
      field: 'CreatedDate',
      width: 180,
      cellRenderer: (params) => formatDate(params.value),
    },
    {
      headerName: 'Modified Date',
      field: 'ModifiedDate',
      cellRenderer: (params) => formatDate(params.value),
    },
    {
      headerName: 'Report Type',
      field: 'ReportTypeId',
      cellRenderer: (params) => getReportTypeName(params.value),
    },
    {
      headerName: 'Target Share Classes',
      cellRenderer: (params) => getTargetName(params.data),
    },
  ];

  const handleCreateFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewFile(file);
      setNewFileName(file.name);
    }
  };

  const handleEditFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditFile(file);
      setEditFileName(file.name);
    }
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!newTemplate.TemplateName.trim()) {
      context.handlers.setToast({ type: 'error', body: <>Template name is required</> });
      return;
    }
    // Check for duplicate names
    const existingNames = templateList.map((t) => t.TemplateName);
    if (existingNames.includes(newTemplate.TemplateName.trim())) {
      context.handlers.setToast({ type: 'error', body: <>A template with this name already exists</> });
      return;
    }

    try {
      setSaving(true);
      await saveTemplate(newFile, newFileName, newTemplate);
      context.handlers.setToast({ type: 'success', body: <>&quot;{newTemplate.TemplateName}&quot; Created</> });
      clearForm();
      await loadData();
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>{err.human || 'Failed to save template'}</> });
    } finally {
      setSaving(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editTemplate) return;

    try {
      setSaving(true);
      await saveTemplate(editFile, editFileName, editTemplate);
      context.handlers.setToast({ type: 'success', body: <>&quot;{editTemplate.TemplateName}&quot; Updated</> });
      setEditModalVisible(false);
      await loadData();
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>{err.human || 'Failed to update template'}</> });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.TemplateId) return;

    try {
      setDeleting(true);
      await deleteTemplate(deleteTarget.TemplateId);
      context.handlers.setToast({ type: 'success', body: <>&quot;{deleteTarget.TemplateName}&quot; Deleted</> });
      setDeleteModalVisible(false);
      await loadData();
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>{err.human || 'Failed to delete template'}</> });
    } finally {
      setDeleting(false);
    }
  };

  const handleEditClick = (template) => {
    setEditTemplate({ ...template });
    setEditFile(null);
    setEditFileName('');
    setEditModalVisible(true);
  };

  const handleDeleteClick = (template) => {
    setDeleteTarget(template);
    setDeleteModalVisible(true);
  };

  const clearForm = () => {
    setNewTemplate({
      TemplateName: '',
      ReportTypeId: '',
      AllFunds: true,
      ScopeMap: [],
      LanguageMap: [],
    });
    setNewFile(null);
    setNewFileName('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleNewFieldChange = (field, value) => {
    setNewTemplate((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditFieldChange = (field, value) => {
    setEditTemplate((prev) => ({ ...prev, [field]: value }));
  };

  const renderTemplateForm = (template, onFieldChange, onFileChange, fileRef, fileName, isEdit) => (
    <div className={classes.formFields}>
      <div className={classes.formGroup}>
        <label>Template Name</label>
        <input
          type="text"
          value={template.TemplateName || ''}
          onChange={(e) => onFieldChange('TemplateName', e.target.value)}
          className={classes.formInput}
          required
        />
      </div>
      <div className={classes.formGroup}>
        <label>Report Type</label>
        <select
          value={template.ReportTypeId || ''}
          onChange={(e) => onFieldChange('ReportTypeId', parseInt(e.target.value, 10) || '')}
          className={classes.formSelect}
        >
          <option value="">Select Report Type</option>
          {reportTypes.map((rt) => (
            <option key={rt.ReportTypeId} value={rt.ReportTypeId}>
              {rt.Label || rt.ReportTypeName}
            </option>
          ))}
        </select>
      </div>
      <div className={classes.formGroup}>
        <label>Template File</label>
        <input
          ref={fileRef}
          type="file"
          accept=".doc,.docx,.html,.htm"
          onChange={onFileChange}
        />
        {fileName && <span className={classes.fileName}>{fileName}</span>}
      </div>
      <div className={classes.formGroup}>
        <label>
          <input
            type="checkbox"
            checked={template.AllFunds ?? true}
            onChange={(e) => onFieldChange('AllFunds', e.target.checked)}
          />
          {' '}All Funds (Default)
        </label>
      </div>
    </div>
  );

  if (loading) {
    return <GrpSpinner />;
  }

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <h2>PDF Template Management</h2>
      </div>

      {/* Templates Grid */}
      <div className={classes.section}>
        <h3>Templates</h3>
        <p className={classes.description}>
          A template can be a Microsoft Word document or HTML file used to dynamically populate data for PDF generation.
        </p>
        <div className={`ag-theme-reborn ${classes.gridWrapper}`}>
          <AgGridReact
            ref={gridRef}
            rowData={templateList}
            columnDefs={columnDefs}
            defaultColDef={{
              sortable: true,
              resizable: true,
              filter: true,
            }}
            domLayout="autoHeight"
          />
        </div>
      </div>

      {/* Create Template Form */}
      <div className={classes.section}>
        <h3>Create a Template</h3>
        <p className={classes.description}>
          Add a new template to dynamically populate your PDF reports.
        </p>
        <form onSubmit={handleCreateSubmit}>
          {renderTemplateForm(newTemplate, handleNewFieldChange, handleCreateFileChange, fileInputRef, newFileName, false)}
          <div className={classes.formButtons}>
            <GrpButton size="Medium" type="Secondary" text="Clear" onClick={clearForm} />
            <GrpButton
              size="Medium"
              type="Primary"
              text={saving ? 'Creating...' : 'Create'}
              onClick={handleCreateSubmit}
              disabled={saving}
            />
          </div>
        </form>
      </div>

      {/* Edit Modal */}
      {editModalVisible && editTemplate && (
        <div className={classes.modalOverlay} onClick={() => setEditModalVisible(false)}>
          <div className={classes.modal} onClick={(e) => e.stopPropagation()}>
            <div className={classes.modalHeader}>
              <strong>Editing Template: {editTemplate.TemplateName}</strong>
              <span
                className={classes.modalClose}
                onClick={() => setEditModalVisible(false)}
              >&times;</span>
            </div>
            <div className={classes.modalBody}>
              <form onSubmit={handleEditSubmit}>
                {renderTemplateForm(editTemplate, handleEditFieldChange, handleEditFileChange, editFileInputRef, editFileName, true)}
                <div className={classes.formButtons}>
                  <GrpButton size="Medium" type="Secondary" text="Cancel" onClick={() => setEditModalVisible(false)} />
                  <GrpButton
                    size="Medium"
                    type="Primary"
                    text={saving ? 'Updating...' : 'Update'}
                    onClick={handleEditSubmit}
                    disabled={saving}
                  />
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalVisible && deleteTarget && (
        <div className={classes.modalOverlay} onClick={() => setDeleteModalVisible(false)}>
          <div className={classes.modal} onClick={(e) => e.stopPropagation()}>
            <div className={classes.modalHeader}>
              <strong>Delete &quot;{deleteTarget.TemplateName}&quot;?</strong>
              <span
                className={classes.modalClose}
                onClick={() => setDeleteModalVisible(false)}
              >&times;</span>
            </div>
            <div className={classes.modalBody}>
              <p>Are you sure you want to delete &quot;{deleteTarget.TemplateName}&quot;?</p>
              <div className={classes.formButtons}>
                <GrpButton size="Medium" type="Secondary" text="Cancel" onClick={() => setDeleteModalVisible(false)} />
                <GrpButton
                  size="Medium"
                  type="Danger"
                  text={deleting ? 'Deleting...' : 'Delete'}
                  onClick={handleDeleteConfirm}
                  disabled={deleting}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateManagement;
